// src/crm/api.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { ContactType, Creator } from '../lib/types'

// ── Clés de cache ──────────────────────────────────────────────────────────
const creatorKeys = {
  all: ['creators'] as const,
  list: (q?: string) => ['creators', 'list', q ?? ''] as const,
  detail: (id: string) => ['creators', 'detail', id] as const,
}

// ── Queries ────────────────────────────────────────────────────────────────

// `| undefined` explicite : exactOptionalPropertyTypes est activé (les appelants
// passent des states qui peuvent être vides, jamais absents).
export type CreatorFilters = {
  type?: ContactType | '' | undefined
  niche?: string | undefined
  country?: string | undefined
}

/**
 * Liste tous les contacts (globaux, non filtrés par app).
 * @param q Filtre optionnel sur le nom (display_name LIKE %q%).
 * @param filters Filtres optionnels type de contact / niche / pays. Les chaînes
 *                vides sont ignorées, ce qui garde l'appel à un seul argument valide.
 */
export function useCreators(q?: string, filters: CreatorFilters = {}) {
  const params = new URLSearchParams()
  if (q) params.set('q', q)
  if (filters.type) params.set('type', filters.type)
  if (filters.niche) params.set('niche', filters.niche)
  if (filters.country) params.set('country', filters.country)
  const qs = params.size > 0 ? `?${params.toString()}` : ''
  return useQuery<Creator[], Error>({
    queryKey: [
      ...creatorKeys.list(q),
      filters.type ?? '',
      filters.niche ?? '',
      filters.country ?? '',
    ],
    queryFn: () =>
      api.get<{ creators: Creator[] }>(`/api/creators${qs}`).then((r) => r.creators),
    staleTime: 1000 * 60 * 2,
  })
}

/**
 * Détail d'un créateur (inclut socials et deals).
 */
export function useCreator(id: string) {
  return useQuery<Creator, Error>({
    queryKey: creatorKeys.detail(id),
    queryFn: () =>
      api.get<{ creator: Creator }>(`/api/creators/${id}`).then((r) => r.creator),
    staleTime: 1000 * 60 * 2,
    enabled: Boolean(id),
  })
}

// ── Mutations ──────────────────────────────────────────────────────────────

export type CreateCreatorInput = {
  displayName: string
  collaborationType: 'audience' | 'production' | 'both'
  contactType?: ContactType
  organization?: string | null
  contactRole?: string | null
  niche?: string | null
  audienceCountry?: string | null
  audienceLanguage?: string | null
  defaultRate?: number | null
  currency?: string
  email?: string | null
  notes?: string | null
  source: 'manual' | 'application' | 'url'
  socials?: {
    platform: string
    handle?: string | null
    profileUrl?: string | null
    followers?: number | null
    engagementRate?: number | null
  }[]
}

export type UpdateCreatorInput = CreateCreatorInput

/**
 * Crée un nouveau créateur.
 */
export function useCreateCreator() {
  const queryClient = useQueryClient()
  return useMutation<Creator, Error, CreateCreatorInput>({
    mutationFn: (body) =>
      api.post<{ creator: Creator }>('/api/creators', body).then((r) => r.creator),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: creatorKeys.all })
    },
  })
}

/**
 * Met à jour un créateur existant (remplace aussi les socials).
 */
export function useUpdateCreator(id: string) {
  const queryClient = useQueryClient()
  return useMutation<Creator, Error, UpdateCreatorInput>({
    mutationFn: (body) =>
      api.put<{ creator: Creator }>(`/api/creators/${id}`, body).then((r) => r.creator),
    onSuccess: (updated) => {
      queryClient.setQueryData(creatorKeys.detail(id), updated)
      void queryClient.invalidateQueries({ queryKey: creatorKeys.all })
    },
  })
}

/**
 * Supprime un créateur.
 */
export function useDeleteCreator() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.del<void>(`/api/creators/${id}`),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: creatorKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: creatorKeys.all })
    },
  })
}
